import Select from 'react-select';
import EventBus from 'eventing-bus';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import { bulkUpdateObjects } from '../../store/Objects';

const BulkAssignModal = ({ isOpen, onClose, objectSchema, selectedIds, onRefresh, onClearSelection }) => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Get members from currentWorkspace
  const members = currentWorkspace?.['members'] || [];
  
  // Format members for react-select
  const memberOptions = [
    { value: null, label: 'No owner' },
    ...members.map(member => ({
      value: member['_id'],
      label: `${member['firstName'] || ''} ${member['lastName'] || ''}`.trim() || member['email'],
      email: member['email']
    }))
  ];

  const handleUpdate = () => {
    if (!objectSchema) return
    
    setIsUpdating(true)
    dispatch(bulkUpdateObjects({
      objectType: objectSchema['type'],
      ids: selectedIds,
      updates: { ownerId: selectedOwner },
      successCallback: () => {
        EventBus.publish('success', `Successfully assigned ${selectedIds.length} ${objectSchema['label'].toLowerCase()}${selectedIds.length > 1 ? 's' : ''}`)
        setIsUpdating(false)
        // Only clear selection on successful update
        setSelectedOwner(null)
        onClose()
        onClearSelection()
        if (onRefresh) onRefresh()
      },
      failCallback: (error) => {
        EventBus.publish('error', error?.message || 'Failed to assign records')
        setIsUpdating(false)
      }
    }))
  }

  const handleClose = () => {
    // Reset form state but don't clear selection on cancel
    setSelectedOwner(null)
    onClose()
    // Commented out: onClearSelection() - Don't clear selection on cancel, only on success
    // onClearSelection()
  }

  // Custom option rendering to show email
  const formatOptionLabel = ({ label, email }) => (
    <div>
      <div>{label}</div>
      {email && <div style={{ fontSize: '12px', color: '#888' }}>{email}</div>}
    </div>
  )

  return (
    <Modal isOpen={isOpen} toggle={handleClose} keyboard={true} className="main-modal modal-dialog-centered new-user-modal bulkedit-modal">
      <ModalHeader toggle={handleClose}></ModalHeader>
      <ModalBody>
        <div className="content-area">
          <h4> Bulk assign {selectedIds.length} {objectSchema?.['label'].toLowerCase() || 'record'}{selectedIds.length > 1 ? 's' : ''}</h4>
          <div className="form-area">
            <div className="form-group">
              <label className="form-label">{objectSchema?.['label'] || 'Record'} owner</label>
              <Select
                value={memberOptions.find(opt => opt['value'] === selectedOwner)}
                onChange={(option) => setSelectedOwner(option?.['value'])}
                options={memberOptions}
                placeholder="Select owner"
                isClearable={false}
                isSearchable
                className="react-select-container"
                classNamePrefix="react-select"
                formatOptionLabel={formatOptionLabel}
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: 'var(--sa-dark-surface-light)',
                    borderColor: '#00BFA5',
                    minHeight: '48px',
                    '&:hover': { borderColor: '#00A693' }
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: 'var(--sa-dark-surface-light)',
                    border: '1px solid var(--sa-dark-border)'
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state['isFocused'] ? 'var(--sa-dark-surface-light)' : 'var(--sa-dark-surface-light)',
                    color: 'var(--sa-dark-text)',
                    '&:active': { backgroundColor: 'var(--sa-dark-surface-light)' }
                  }),
                  singleValue: (base) => ({ ...base, color: 'var(--sa-dark-text)' }),
                  placeholder: (base) => ({ ...base, color: '#999' }),
                  input: (base) => ({ ...base, color: 'var(--sa-dark-text)' })
                }}
              />
            </div>

            <div className="btns-box text-right mt-4">
              <button 
                className="btn-style-border mr-3" 
                onClick={handleClose}
                disabled={isUpdating}
                style={{ marginRight: '10px' }}
              >
                Cancel
              </button>
              <button
                className="btn-style-new"
                onClick={handleUpdate}
                disabled={isUpdating}
                style={{ 
                  opacity: isUpdating ? 0.7 : 1
                }}
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default BulkAssignModal;